<template>
  <section>
    <h2>{{ $_translations.value.page_profile_edit_section_general_info_title }}</h2>
  </section>

  <TransparentCard container-tag="section">
    <AvatarSelectionCarousel
      :model-value="authStore.userAvatar"
      @update:modelValue="handleAvatarChange"
    />
  </TransparentCard>

  <section class="edit_options">
    <SettingsButtonItem
      text="page_profile_edit_settings_item_change_username"
      :disabled="isChangeUsernameDisabled"
      @click="openChangeUsernameModal"
    />

    <SettingsButtonItem
      v-if="!appSettingsStore.appProperties.hideChangePassword"
      text="page_profile_edit_settings_item_change_password"
      @click="openChangePasswordModal"
    />

    <SettingsButtonItem
      v-if="isRegisterSettingsItemVisible"
      text="page_profile_edit_settings_item_submit_email"
      :disabled="isRegisterEmailDisabled"
      @click="handleShowRegisterEmail"
    />

    <SettingsToggleItem
      text="page_profile_edit_settings_item_private_account"
      :selected="authStore.user.hasPrivateProfile"
      :options="profileVisibilityOptions"
      @update:selected="handleUserProfileVisibilityChange"
    />
  </section>

  <ChangeUsernameModal v-if="isChangeUsernameModalOpen" />

  <ChangePasswordModal
    v-else-if="isChangePasswordModalOpen"
    @redirect:forgotPassword="handleForgotPasswordRedirectFromChangePasswordModal"
  />

  <RegisterEmail
    v-if="isRegisterEmailVisible"
    @registerEmail:resolved="handleRegisterEmailResolved"
  />
</template>

<script setup>
import { computed, ref } from 'vue';
import TransparentCard from '@/components/card/TransparentCard.vue';
import AvatarSelectionCarousel from '@/components/carousel/AvatarSelectionCarousel.vue';
import RegisterEmail from '@/components/email/RegisterEmail.vue';
import SettingsButtonItem from '@/views/profileEdit/components/item/SettingsButtonItem.vue';
import SettingsToggleItem from '@/views/profileEdit/components/item/SettingsToggleItem.vue';
import ChangeUsernameModal from '@/views/profileEdit/components/modal/ChangeUsernameModal.vue';
import ChangePasswordModal from '@/views/profileEdit/components/modal/ChangePasswordModal.vue';
import useAppSettingsStore from '@/store/modules/appSettings.js';
import useAuthStore from '@/store/modules/auth.js';
import useModalHook from '@/hooks/useModal.js';
import useForgotPasswordHook from '@/hooks/useForgotPassword.js';

const profileVisibilityOptions = [
  { id: 'on', value: true, label: 'page_profile_edit_settings_option_on' },
  { id: 'off', value: false, label: 'page_profile_edit_settings_option_off' },
];

const appSettingsStore = useAppSettingsStore();
const authStore = useAuthStore();

const [isChangeUsernameModalOpen, openChangeUsernameModal] = useModalHook();
const [isChangePasswordModalOpen, openChangePasswordModal, closeChangePasswordModal] = useModalHook();
const { redirectToIndexStep: redirectToForgotPasswordPage } = useForgotPasswordHook();

const isRegisterSettingsItemVisible = ref(false); // We will set this based on property that we get from backend in future
const isRegisterEmailVisible = ref(false);

const isChangeUsernameDisabled = computed(() => authStore.user.isUsernameUpdated);
const isRegisterEmailDisabled = computed(() => authStore.user.hasEmailVerified);

const handleAvatarChange = (avatar) => {
  authStore.dispatchChangeAvatar({ avatar });
};

const handleShowRegisterEmail = () => {
  isRegisterEmailVisible.value = true;
};

const handleRegisterEmailResolved = () => {
  isRegisterEmailVisible.value = false;
};

const handleUserProfileVisibilityChange = (visibility) => {
  authStore.dispatchChangeUserProfileVisibility({ visibility });
};

const handleForgotPasswordRedirectFromChangePasswordModal = () => {
  closeChangePasswordModal({
    immediate: true,
    type: 'useCallback',
    afterHideCallback: redirectToForgotPasswordPage,
  });
};
</script>
